
import React, { useMemo } from 'react';
import { Badge } from '@/components/ui/badge';
import { X } from 'lucide-react';
import { DISCIPLINES_SKILLS } from "@/data/constants";
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export interface SkillsCoreProps {
  skills: string[] | null;
  newSkill: string;
  profession: string | null;
  onNewSkillChange: (value: string) => void;
  onAddSkill: () => void;
  onRemoveSkill: (index: number) => void;
  maxSkills?: number;
  showLabel?: boolean;
  className?: string;
}

export const SkillsCore = ({
  skills,
  newSkill,
  profession,
  onNewSkillChange,
  onAddSkill,
  onRemoveSkill,
  maxSkills = 6,
  showLabel = true,
  className = ""
}: SkillsCoreProps) => {
  const trimmedNewSkill = newSkill.trim();
  const hasReachedLimit = (skills?.length || 0) >= maxSkills;
  const isAddDisabled =
    hasReachedLimit ||
    trimmedNewSkill.length === 0 ||
    !!skills?.find(skill => skill.toLowerCase() === trimmedNewSkill.toLowerCase());

  // Get suggested skills based on selected profession
  const suggestedSkills = useMemo(() => {
    if (!profession) return [];

    // Find the discipline in the map
    const disciplineSkills = DISCIPLINES_SKILLS.find(
      d => d.discipline === profession
    );

    return disciplineSkills?.skills || [];
  }, [profession]);
  
  // Filter out already selected skills from suggestions
  const availableSuggestions = useMemo(() => {
    if (!skills) return suggestedSkills;
    return suggestedSkills.filter(skill => !skills.includes(skill));
  }, [suggestedSkills, skills]);

  const handleSelectSkill = (skill: string) => {
    if (skill && !skills?.includes(skill) && (skills?.length || 0) < maxSkills) {
      // Set the skill value in the parent state
      onNewSkillChange(skill);
      // Use setTimeout to ensure the state is updated before calling onAddSkill
      setTimeout(() => {
        onAddSkill();
      }, 0);
    }
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className={`space-y-4 ${className}`}>
          <div className="flex gap-2">
            <Select
              value=""
              onValueChange={handleSelectSkill}
              disabled={!profession || hasReachedLimit}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={profession ? "Select a skill" : "Select your profession first"} />
              </SelectTrigger>
              <SelectContent>
                <div className="max-h-[300px] overflow-y-auto">
                  {availableSuggestions.map((skill, index) => (
                    <SelectItem key={index} value={skill}>
                      {skill}
                    </SelectItem>
                  ))}
                  {availableSuggestions.length === 0 && (
                    <div className="px-2 py-3 text-sm text-muted-foreground">
                      {profession ? "No suggested skills available" : "Select your profession first"}
                    </div>
                  )}
                </div>
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-2">
            <Input
              value={newSkill}
              onChange={(e) => onNewSkillChange(e.target.value)}
              placeholder="Add a skill"
              disabled={hasReachedLimit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (!isAddDisabled) {
                    onAddSkill();
                  }
                }
              }}
            />
            <Button
              type="button"
              onClick={onAddSkill}
              disabled={isAddDisabled}
              variant="secondary"
            >
              Add
            </Button>
          </div>

          <div className="bg-white flex flex-wrap gap-2 mb-2">
            {skills && skills.map((skill, index) => (
              <Badge key={index} className="group px-3 py-1.5 flex items-center gap-1.5 bg-[#F4F4F5] text-black hover:text-white">
                {skill}
                <button
                  type="button"
                  onClick={() => onRemoveSkill(index)}
                  className="ml-1 text-black group-hover:text-white"
                >
                  <X className="h-3 w-3" />
                  <span className="sr-only">Remove {skill}</span>
                </button>
              </Badge>
            ))}
            {skills?.length === 0 && (
              <p className="text-sm text-muted-foreground">No skills added yet.</p>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {hasReachedLimit
              ? `You’ve added the maximum of ${maxSkills} skills.`
              : `You can add up to ${maxSkills} skills.`}
          </p>
        </div>
      </CardContent>
    </Card>

  );
};

export default SkillsCore;
